import { z } from "zod";
import { Surface } from "./domain";

// §3.4 Pipeline-DAG — vollständig „Daten", typisierte Refs (D15/D30/D34/D38/D41) ──

export const Literal = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type Literal = z.infer<typeof Literal>;

// strikt, damit die Union disjunkt ist: ohne .strict() würde ein
// { node, port, collection:true } bereits als ArtifactRef parsen und
// `collection` still verwerfen (D41/D42-Bruch). CollectionRef wird zuerst geprüft.

/** node:'$input' = Job-Input-Asset per role; ordinal optional (Fan-out-Element) */
export const ArtifactRef = z
  .object({
    node: z.string(),
    port: z.string(),
    ordinal: z.number().int().optional(),
  })
  .strict();
export type ArtifactRef = z.infer<typeof ArtifactRef>;

/** ganzes geordnetes Array (Fan-out-Quelle/-Ergebnis, D42) */
export const CollectionRef = z
  .object({
    node: z.string(),
    port: z.string(),
    collection: z.literal(true),
  })
  .strict();
export type CollectionRef = z.infer<typeof CollectionRef>;

export const Ref = z.union([CollectionRef, ArtifactRef]);
export type Ref = z.infer<typeof Ref>;

/**
 * Param-Herkunft (D41): aus Vorgänger-Output (from:'node' → node+port) ODER
 * settings[key] (from:'settings' → key). superRefine erzwingt die Semantik,
 * ohne den von der PRD §3.4 vorgegebenen offenen Objekttyp zu verändern.
 */
export const ScalarRef = z
  .object({
    from: z.enum(["node", "settings"]),
    node: z.string().optional(),
    port: z.string().optional(),
    key: z.string().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.from === "node") {
      if (!val.node || !val.port)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ScalarRef from:'node' braucht node+port (D41)" });
      if (val.key !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ScalarRef from:'node' darf kein key haben (D41)" });
    } else {
      if (!val.key)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ScalarRef from:'settings' braucht key (D41)" });
      if (val.node !== undefined || val.port !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ScalarRef from:'settings' darf node/port nicht haben (D41)" });
    }
  });
export type ScalarRef = z.infer<typeof ScalarRef>;

/** params-Wert: Literal ODER ScalarRef (kein Speziallogik-Param, D41) */
export const ParamValue = z.union([ScalarRef, Literal]);
export type ParamValue = z.infer<typeof ParamValue>;

export const StepCondition = z.object({
  fromNode: z.string(), // DAG-Abhängigkeitskante (build-validiert)
  signal: z.string(), // schema-validierter Output-Key von fromNode
  op: z.enum(["truthy", "eq", "neq"]),
  value: Literal.optional(),
  // 'run' nur explizit geprüft (D41); skipped/failed Upstream ⇒ Signal = MISSING
  onMissing: z.enum(["skip", "runIfBackcompat"]),
});
export type StepCondition = z.infer<typeof StepCondition>;

export const PipelineNode = z.object({
  id: z.string(),
  capabilityId: z.string(),
  capabilityVersion: z.number().int(), // GEPINNT
  surface: Surface,
  inputs: z.record(z.string(), Ref), // benannte Artefakt-Ports
  params: z.record(z.string(), ParamValue).optional(),
  fanOut: z.object({ over: CollectionRef }).optional(), // Knoten läuft je Element → Output = Collection
  condition: StepCondition.optional(),
  passThrough: z.record(z.string(), z.string()).optional(), // condition FALSE: outPort → inPort, typ-rein
});
export type PipelineNode = z.infer<typeof PipelineNode>;

export const OutputBinding = z.object({
  ref: Ref,
  optional: z.boolean().optional(), // optional → Slot darf abwesend/skipped sein (D41)
});
export type OutputBinding = z.infer<typeof OutputBinding>;

export const PipelineSpec = z.object({
  nodes: z.array(PipelineNode),
  outputs: z.record(z.string(), OutputBinding),
});
export type PipelineSpec = z.infer<typeof PipelineSpec>;

/**
 * Aufgelöster Pipeline-Spec + Trace (executed_pipeline_spec, D44): pinnt
 * capabilityVersion (immutable) + Provider/Modellversion + Conditions-Auswertung →
 * exakt nachvollziehbar. `.passthrough()` bewahrt zusätzliche Resolve-Felder beim
 * Round-Trip durch die DB, statt sie auf das engere PipelineSpec zu beschneiden.
 */
export const ExecutedPipelineSpec = PipelineSpec.extend({
  trace: z
    .array(
      z.object({
        nodeId: z.string(),
        status: z.enum(["done", "skipped", "failed"]),
        startedAt: z.string().optional(),
        finishedAt: z.string().optional(),
        provider: z
          .object({ id: z.string(), version: z.number().int(), modelVersion: z.string().optional() })
          .optional(),
        note: z.string().optional(),
      }),
    )
    .optional(),
}).passthrough();
export type ExecutedPipelineSpec = z.infer<typeof ExecutedPipelineSpec>;

/** Plan + Tool-Bindung — Eingabe für die Pricing-Engine (§3.6). */
export interface PricedPlan {
  toolId: string;
  spec: PipelineSpec;
}
